/**
 * Bruno's YAML (OpenCollection) shapes → IR. Default on-disk format since
 * Bruno 3.1, read from `usebruno/bruno` packages/bruno-filestore/src/formats/yml
 * and packages/bruno-converters/src/opencollection:
 *
 *   request item  { info:{name,type,seq}, http|graphql:{method,url,headers,params,body,auth},
 *                   runtime:{variables,scripts,assertions,actions}, settings, docs, examples }
 *   folder.yml    { info:{name,type:'folder',seq}, request:{headers,auth,variables,scripts}, docs }
 *   opencollection.yml / bundled export
 *                 { opencollection, info:{name}, request:{…}, docs, config:{environments}, items }
 *   environment   { name, variables:[{name, value (string | {type,data}), disabled?, secret?}] }
 *
 * Auth: on a request an ABSENT `auth` means none (Bruno drops `mode:none` when
 * it serialises), `'inherit'` is a bare string; on a folder/collection absent
 * means "not set".
 */
import { authFromBrunoModel, emptyDefaults, emptyCounts, row, valueToString } from './ir';

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v == null ? '' : String(v));
const docsOf = (d) => (typeof d === 'string' ? d : d && typeof d === 'object' ? str(d.content) : '');

const kvRows = (items) =>
  arr(items)
    .filter((h) => h && (h.name != null || h.key != null))
    .map((h) => row(h.name ?? h.key, h.value, h.disabled !== true && h.enabled !== false));

/** `params` is an ARRAY of `{name,value,type}` in the serializer, a `{query,path}` map in the docs. */
function readParams(params) {
  if (Array.isArray(params)) {
    return {
      query: kvRows(params.filter((p) => p && (p.type || 'query') === 'query')),
      path: kvRows(params.filter((p) => p && p.type === 'path')),
    };
  }
  if (params && typeof params === 'object') {
    return { query: kvRows(params.query), path: kvRows(params.path) };
  }
  return { query: [], path: [] };
}

/** A body may be one object or a list of variants (`[{title, selected, body}]`). */
function pickVariant(body) {
  if (Array.isArray(body)) {
    const selected = body.find((v) => v && v.selected);
    return (selected || body[0])?.body;
  }
  return body;
}

function readBody(rawBody) {
  const body = pickVariant(rawBody);
  if (!body || typeof body !== 'object') return { mode: 'none' };
  const type = str(body.type).toLowerCase();
  switch (type) {
    case 'json':
    case 'text':
    case 'xml':
    case 'sparql':
      return { mode: type, text: valueToString(body.data) };
    case 'form-urlencoded':
      return { mode: 'urlencoded', rows: kvRows(body.data) };
    case 'multipart-form':
      return {
        mode: 'multipart',
        rows: arr(body.data)
          .filter((f) => f && f.name != null)
          .map((f) => {
            const enabled = f.disabled !== true;
            if (f.type === 'file') {
              const paths = Array.isArray(f.value) ? f.value : [f.value];
              return { key: str(f.name), value: '', enabled, type: 'file', src: paths.filter(Boolean).map(String).join('|') };
            }
            return { key: str(f.name), value: valueToString(f.value), enabled, type: 'text' };
          }),
      };
    case 'file':
      return { mode: 'file' };
    default:
      return { mode: 'none' };
  }
}

/** OpenCollection auth → Bruno's model → IR. */
export function ymlAuthToModel(auth, { absent = 'none' } = {}) {
  if (auth == null) return { mode: absent };
  if (typeof auth === 'string') return { mode: auth === 'inherit' ? 'inherit' : absent };
  const type = str(auth.type).toLowerCase();
  switch (type) {
    case 'none':
    case 'inherit':
      return { mode: type };
    case 'bearer':
      return { mode: type, bearer: { token: auth.token } };
    case 'basic':
      return { mode: type, basic: { username: auth.username, password: auth.password } };
    case 'apikey':
      return { mode: type, apikey: { key: auth.key, value: auth.value, placement: auth.placement } };
    case 'awsv4':
      return { mode: type, awsv4: auth };
    case 'oauth2': {
      const flow = str(auth.flow);
      const pkce = auth.pkce;
      return {
        mode: 'oauth2',
        oauth2: {
          grantType: flow === 'resource_owner_password_credentials' ? 'password' : flow || 'authorization_code',
          authorizationUrl: auth.authorizationUrl,
          accessTokenUrl: auth.accessTokenUrl,
          callbackUrl: auth.callbackUrl,
          clientId: auth.credentials?.clientId ?? auth.clientId,
          clientSecret: auth.credentials?.clientSecret ?? auth.clientSecret,
          scope: auth.scope,
          // `pkce: {method:'S256'}` / `{}` = on, `{disabled:true}` / absent = off.
          pkce: pkce === true || (!!pkce && typeof pkce === 'object' && pkce.disabled !== true),
        },
      };
    }
    default:
      return { mode: type || 'unsupported' };
  }
}

function readScripts(scripts) {
  const out = { pre: '', post: '', tests: '' };
  for (const s of arr(scripts)) {
    if (!s || typeof s.code !== 'string') continue;
    if (s.type === 'before-request') out.pre = s.code.trim();
    else if (s.type === 'after-response') out.post = s.code.trim();
    else if (s.type === 'tests') out.tests = s.code.trim();
  }
  return out;
}

const seqOf = (v) => (Number.isFinite(Number(v)) && v !== '' && v != null ? Number(v) : undefined);

/** One request item (a `.yml` request file, or an entry of a bundle's `items`). */
export function ymlItemToNode(item, fallbackName = '') {
  const info = (item && item.info) || {};
  const type = str(info.type || (item?.graphql ? 'graphql' : 'http')).toLowerCase();
  const name = str(info.name) || fallbackName || 'Untitled Request';
  const block = type === 'graphql' ? item?.graphql : item?.http;
  if ((type !== 'http' && type !== 'graphql') || !block || typeof block !== 'object') {
    return { kind: 'request', name, seq: seqOf(info.seq), reqType: 'unsupported', typeName: type };
  }
  const params = readParams(block.params);
  const runtime = item.runtime || {};
  let body;
  if (type === 'graphql') {
    const gql = pickVariant(block.body) || {};
    body = { mode: 'graphql', graphql: { query: valueToString(gql.query), variables: valueToString(gql.variables) } };
  } else {
    body = readBody(block.body);
  }
  return {
    kind: 'request',
    name,
    seq: seqOf(info.seq),
    reqType: type,
    method: str(block.method || (type === 'graphql' ? 'POST' : 'GET')).toUpperCase(),
    url: str(block.url),
    headers: kvRows(block.headers),
    query: params.query,
    pathParams: params.path,
    body,
    auth: authFromBrunoModel(ymlAuthToModel(block.auth, { absent: 'none' })),
    scripts: readScripts(runtime.scripts),
    docs: docsOf(item.docs),
    counts: {
      ...emptyCounts(),
      vars: arr(runtime.variables).length + arr(runtime.actions).length,
      assertions: arr(runtime.assertions).length,
      examples: arr(item.examples).length,
    },
  };
}

/** `request:` defaults of a folder / collection. */
export function ymlDefaults(request) {
  const d = emptyDefaults();
  if (!request || typeof request !== 'object') return d;
  d.headers = kvRows(request.headers);
  if (request.auth != null) {
    const model = ymlAuthToModel(request.auth, { absent: 'none' });
    d.auth = authFromBrunoModel(model);
  }
  d.vars = kvRows(request.variables);
  d.scripts = readScripts(request.scripts);
  return d;
}

/** `folder.yml` → `{ name, seq, docs, defaults }`. */
export function ymlFolderMeta(obj) {
  const info = (obj && obj.info) || {};
  return { name: str(info.name), seq: seqOf(info.seq), docs: docsOf(obj?.docs), defaults: ymlDefaults(obj?.request) };
}

/** An OpenCollection environment → ImportedEnv. Secrets carry no value (O7). */
export function ymlEnvToEnv(env, fallbackName = '') {
  const variables = arr(env?.variables)
    .filter((v) => v && v.name != null && String(v.name) !== '')
    .map((v) => ({
      key: String(v.name),
      value: v.secret === true ? '' : valueToString(v.value),
      enabled: v.disabled !== true,
      secret: v.secret === true,
    }));
  return { name: str(env?.name) || fallbackName || 'Environment', variables };
}

function itemsToNodes(items) {
  return arr(items)
    .filter((it) => it && typeof it === 'object')
    .map((it) => {
      if (str(it.info?.type).toLowerCase() === 'folder') {
        const meta = ymlFolderMeta(it);
        return {
          kind: 'folder',
          name: meta.name || 'Folder',
          seq: meta.seq,
          docs: meta.docs,
          defaults: meta.defaults,
          children: itemsToNodes(it.items),
        };
      }
      return ymlItemToNode(it);
    });
}

/** A bundled OpenCollection export (one `.yml` with every item inline). */
export function openCollectionToIR(obj, fallbackName = '') {
  return {
    name: str(obj?.info?.name) || fallbackName || 'Bruno Collection',
    docs: docsOf(obj?.docs),
    defaults: ymlDefaults(obj?.request),
    children: itemsToNodes(obj?.items),
    environments: arr(obj?.config?.environments).map((e) => ymlEnvToEnv(e)),
  };
}
